// Lightweight global task tracker for "what is the system doing right now"
// indicators. Vanilla useSyncExternalStore implementation — no Zustand or
// other dependency required.

import { useSyncExternalStore } from "react";

const listeners = new Set();
let snapshot = { tasks: [], updatedAt: 0 };

const emit = () => {
  snapshot = { tasks: [...tasks.values()], updatedAt: Date.now() };
  listeners.forEach((listener) => listener());
};

const tasks = new Map();

export const TASK_KIND = Object.freeze({
  ANALYSIS: "analysis",
  ANALYSIS_BATCH: "analysis-batch",
  META_REFRESH: "meta-refresh",
  TAGGING: "tagging",
  CLUSTERING: "clustering",
  TRANSCRIBE: "transcribe",
  THESIS: "thesis",
  CHAT: "chat",
});

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => snapshot;

export const startTask = (id, { kind, label, total = 0, parentId = null } = {}) => {
  if (!id) return;
  tasks.set(id, {
    id,
    kind: kind || "task",
    label: label || id,
    current: 0,
    total,
    status: "running",
    error: null,
    parentId,
    startedAt: Date.now(),
    finishedAt: null,
  });
  emit();
};

export const progressTask = (id, current, partial = {}) => {
  const task = tasks.get(id);
  if (!task) return;
  tasks.set(id, {
    ...task,
    ...partial,
    current: typeof current === "number" ? current : task.current,
  });
  emit();
};

export const finishTask = (id, status = "done", details = {}) => {
  const task = tasks.get(id);
  if (!task) return;
  tasks.set(id, {
    ...task,
    ...details,
    status,
    finishedAt: Date.now(),
  });
  emit();
  // Auto-clear successful tasks after a short delay so they linger long
  // enough to be acknowledged. Errors stay until dismissed.
  if (status === "done") {
    setTimeout(() => {
      const cur = tasks.get(id);
      if (cur && cur.status === "done") {
        tasks.delete(id);
        emit();
      }
    }, 4000);
  }
};

export const dismissTask = (id) => {
  if (tasks.delete(id)) emit();
};

export const dismissTasksByKind = (kind) => {
  let changed = false;
  tasks.forEach((task, id) => {
    if (task.kind === kind && task.status !== "running") {
      tasks.delete(id);
      changed = true;
    }
  });
  if (changed) emit();
};

export const useStatusTasks = () =>
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

export const wrapWithStatus = async (id, meta, fn) => {
  startTask(id, meta);
  try {
    const result = await fn();
    finishTask(id, "done");
    return result;
  } catch (error) {
    finishTask(id, "error", { error: String(error?.message || error) });
    throw error;
  }
};
